import React, { useState, useEffect, useMemo } from 'react';
import { Button, Typography, Container, Divider, Drawer } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import {ColorSelect, ColorBlock} from './ColorSelect'
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Fab from '@mui/material/Fab';
import Box from '@mui/material/Box';
import Grid2 from '@mui/material/Unstable_Grid2';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Hood from './Hood';
import { WORKER_URL } from './SvgTemplate';
import { pureShuffleConfig } from './shuffleRules';
import ItemPicker from './ItemPicker';
import qrcode from './qrcode.png';
import SaveLoadPopUp from './SaveLoadPopUp'
import {defaultConfig, defaultBlackConfigLight, defaultBlackConfigNonLight, decode, encode, encodeBlackConfig, decodeBlackConfig, buttonColorList} from './ConfigCode'
import InputDialog from './InputDialog';

import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';

import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

import LOGO from './logo.png'
import MuzzleDefault from './muzzle-default.png'
import MuzzleFur from './muzzle-fur.png'

import EyeDefault from './eye-default.png'
import EyeDou from './eye-dou.png'
import EyeFrame from './eye-frame.png'

import AddedMuzzleDefault from './added-muzzle-default.png'
import AddedMuzzleFur from './added-muzzle-fur.png'

import MuzzleAssoicateDefault from './muzzle-assoicate-default.png'
import MuzzleAssoicateMustache from './muzzle-assoicate-mustache.png'
import MuzzleAssoicateFur from './muzzle-assoicate-fur.png'

import FurMuzzleAssoicateDefault from './fur-muzzle-assoicate-default.png'
import FurMuzzleAssoicateMustache from './fur-muzzle-assoicate-mustache.png'
import FurMuzzleAssoicateFur from './fur-muzzle-assoicate-fur.png'

import SpikesStyleAFur from './spikes-style-a-fur.png'
import SpikesStyleBFur from './spikes-style-b-fur.png'
import SpikesStyleAMuzzle from './spikes-style-a-muzzle.png'
import SpikesStyleBMuzzle from './spikes-style-b-muzzle.png'

import MuzzleDefaultDefault from './muzzle-default-default.png'
import MuzzleFurDefault from './muzzle-fur-default.png'
import MuzzleLip from './muzzle-lip.png'

import Ear from './ear.png'
import MuzzleEar from './muzzle-ear.png';

function App() {
  const { t } = useTranslation();
  const queryParameters =  useMemo(() => new URLSearchParams(window.location.search), []);
  const code = queryParameters.get("code")
  const printModeParam = queryParameters.get("print")
  const clientNameParam = queryParameters.get("client_name")
  const clientIdParam = queryParameters.get("client_id")
  const orderDateParam = queryParameters.get("order_date")
  const blackConfigParam = queryParameters.get("black_config")
  const editorModeParam = queryParameters.get("editor_mode")

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [shuffling, setShuffling] = useState(false);
  const [printMode, setPrintMode] = useState(printModeParam === "true" ? true : false);
  const [config, setConfig] = useState(code ? decode(code) : defaultConfig);
  const decodedBlackConfig = decodeBlackConfig(blackConfigParam)
  const [blackConfig, setBlackConfig] = useState(decodedBlackConfig ? decodedBlackConfig : config['black-type-light'] ? defaultBlackConfigLight : defaultBlackConfigNonLight);
  
  const [customerInfo, setCustomerInfo] = useState(
    {
      "client_name": clientNameParam && decodeURIComponent(clientNameParam) ? decodeURIComponent(clientNameParam) : "",
      "client_id": clientIdParam && decodeURIComponent(clientIdParam) ? decodeURIComponent(clientIdParam) : "",
      "order_date": orderDateParam && decodeURIComponent(orderDateParam) ? decodeURIComponent(orderDateParam) : new Date().toLocaleDateString()
    }
  )

  const [editorMode, setEditorMode] = useState(editorModeParam && editorModeParam === "muzzle" ? "muzzle" : "hood");

  useEffect(() => {
    let pressCount = 0;
    let pressTimer = null;

    const handleKeyDown = (event) => {
      if (event.key === 'p') {
        pressCount += 1;

        if (pressTimer) {
          clearTimeout(pressTimer);
        }

        pressTimer = setTimeout(() => {
          pressCount = 0; // Reset the count after 3 seconds of inactivity
        }, 3000);

        if (pressCount === 5) { // Check if P is pressed 5 times
          queryParameters.set('print', !printMode ? 'true' : 'false');
          window.history.replaceState(null, '', "?" + queryParameters.toString());
          setPrintMode((prevMode) => !prevMode);
          pressCount = 0; // Reset the count after toggling
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [printMode, queryParameters]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleResetToDefault = () => {
    changeHistory(defaultConfig);
    setConfig(defaultConfig);
  };

  // Shuffle — random color + style suggestion.
  // Backend first (worker /api/shuffle → Colormind API w/ pure-random fallback);
  // if the backend itself is unreachable, run the pure shuffle locally.
  // URL params stay the source of truth (changeHistory + changeBlackConfigHistory).
  const handleShuffle = async () => {
    if (shuffling) return;
    setShuffling(true);
    let newConfig = null;
    let newBlackConfig = null;
    try {
      const res = await fetch(`${WORKER_URL || 'https://editor-worker.rulerbdsm.com'}/api/shuffle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: editorMode,
          config,
          blackConfig,
          blackTypeLight: config['black-type-light'],
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data && data.config && typeof data.config === 'object') {
          newConfig = data.config;
          newBlackConfig = data.blackConfig && typeof data.blackConfig === 'object' ? data.blackConfig : blackConfig;
        }
      }
    } catch (e) {
      console.warn('Shuffle backend unavailable, using local fallback:', e);
    }
    if (!newConfig) {
      const local = pureShuffleConfig({
        config,
        blackConfig,
        blackTypeLight: config['black-type-light'],
        mode: editorMode,
      });
      newConfig = local.config;
      newBlackConfig = local.blackConfig;
    }
    changeHistory(newConfig);
    setConfig(newConfig);
    if (newBlackConfig) {
      changeBlackConfigHistory(newBlackConfig);
      setBlackConfig(newBlackConfig);
    }
    setShuffling(false);
  };

  const handleSelectColor = (key, color) => {
    var newConfig = {...config}
    newConfig[key] = color
    changeHistory(newConfig);
    setConfig(newConfig);
  };

  // Shared color-select helper — one place for the glossy/matte black text, so the
  // button/hood color selectors don't duplicate the same props (easier maintenance).
  const renderColorSelect = (colorKey, { ignore, description, blackText = true, extraText = {}, colorItems } = {}) => (
    <ColorSelect
      defaultColor={config[colorKey]}
      setSelectedColor={(color) => {
        handleSelectColor(colorKey, color)
      }}
      textOverwrite={{
        ...(blackText ? { "black": blackConfig[colorKey] ? t('app.blackTypeGlossyBlack'): t('app.blackTypeMatteBlack') } : {}),
        ...extraText,
      }}
      ignore={ignore}
      description={description}
      colorItems={colorItems}
    />
  );

  const handleSizeChange = (event) => {
    var newConfig = {...config}
    newConfig['hood-size'] = event.target.value;
    changeHistory(newConfig);
    setConfig(newConfig);
  };

  const handleCheckChange = (key, checked) => {
    var newConfig = {...config}
    newConfig[key] = checked
    changeHistory(newConfig);
    setConfig(newConfig);
    if (key === 'black-type-light') {
      queryParameters.delete('black_config');
      window.history.replaceState(null, '', "?" + queryParameters.toString());
      setBlackConfig(checked ? defaultBlackConfigLight : defaultBlackConfigNonLight)
    }
  };

  const handleEyeBrowStyleChange = (style) => {
    var newConfig = {...config}
    newConfig['eyebrow-style'] = style;
    changeHistory(newConfig);
    setConfig(newConfig);
  };

  const handleSpikesStyleChange = (style) => {
    var newConfig = {...config}
    newConfig['spikes-style'] = style;
    changeHistory(newConfig);
    setConfig(newConfig);
  }

  const handleMuzzleStyleChange = (style) => {
    var newConfig = {...config}
    if (style === 'style1' || style === 'style2') {
      // muzzle styles — no added-* prefix (鼻贴 nose patch has its own toggle)
      newConfig['muzzle-style'] = style;
    } else if (newConfig['muzzle-style'].startsWith('added')) {
      newConfig['muzzle-style'] = 'added-' + style;
    } else {
      newConfig['muzzle-style'] = style;
    }
    changeHistory(newConfig);
    setConfig(newConfig);
  };

  const handleNosePatchChange = (on) => {
    var newConfig = {...config}
    newConfig['nose-patch'] = on;
    changeHistory(newConfig);
    setConfig(newConfig);
  };

  const handleMuzzleAddedChange = (added) => {
    var newConfig = {...config}
    if (added) {
      newConfig['muzzle-style'] = 'added-' + newConfig['muzzle-style'];
    } else {
      newConfig['muzzle-style'] = newConfig['muzzle-style'].replace('added-', '');
    }
    changeHistory(newConfig);
    setConfig(newConfig);
  };

  const handleMuzzleAssoicateChange = (style) => {
    var newConfig = {...config}
    newConfig['muzzle-assoicate'] = style;
    changeHistory(newConfig);
    setConfig(newConfig);
  }

  // 圆脸/FUR (+added) use the hood face details; style1/style2 use the muzzle ones.
  const isHoodFaceDetailFamily = (style) =>
    style === 'default' || style === 'fur' || style === 'added-default' || style === 'added-fur';

  const changeHistory = (config) => {
    queryParameters.set('code', encode(config));
    window.history.replaceState(null, '', "?" + queryParameters.toString());
  }

  const handleCustomerInfoChange = (key, value) => {
    var newCustomerInfo = {...customerInfo}
    newCustomerInfo[key] = value;
    changeCustomerInfoHistory(key, value);
    setCustomerInfo(newCustomerInfo)
  }

  const changeCustomerInfoHistory = (key, value) => {
    queryParameters.set(key, encodeURIComponent(value));
    window.history.replaceState(null, '', "?" + queryParameters.toString());
  }

  const handleBlackConfigChange = (key) => {
    var newBlackConfig = {...blackConfig}
    newBlackConfig[key] = !newBlackConfig[key]
    changeBlackConfigHistory(newBlackConfig);
    setBlackConfig(newBlackConfig)
  }

  const changeBlackConfigHistory = (blackConfig) => {
    queryParameters.set('black_config', encodeBlackConfig(blackConfig));
    window.history.replaceState(null, '', "?" + queryParameters.toString());
  }

  const handleEditorChanged = (event, value) => {
    if (value != null){
      if (value === "muzzle") {
        console.log("Switching to muzzle mode");
        let changed = false;
        var newConfig = {...config}
        const constKey = ['bridge-button', 'muzzle-button', 'muzzle-button-b'];
        constKey.forEach((key) => {
          console.log("Checking key: ", key);
          if (newConfig[key] === 'metal') {
            newConfig[key] = 'black';
            changed = true;
          }
        });
        console.log("New config after muzzle mode switch: ", newConfig);
        if (changed) {
          changeHistory(newConfig);
          setConfig(newConfig);
        }
      }
      queryParameters.set('editor_mode', value);
      window.history.replaceState(null, '', "?" + queryParameters.toString());
      setEditorMode(value);
    }
  }

  if (printMode) {
    return (
      <Container style={{height: '9.25in', width: '7in', marginTop: '0mm, 16mm, 0mm, 16mm', overflow: 'hidden'}}>
        <Grid2 container spacing={2}>
          <Grid2 xs={12}>
          <Divider variant="middle">RULER定制款头套工艺单</Divider>
          </Grid2>
          <Grid2 xs={4}>
            <InputDialog keyText="客户姓名" valueText={customerInfo['client_name']} handleChange={(context) => {handleCustomerInfoChange('client_name', context)}}/>
          </Grid2>
          <Grid2 xs={3}>
            <InputDialog keyText="编号" valueText={customerInfo['client_id']} handleChange={(context) => {handleCustomerInfoChange('client_id', context)}}/>
          </Grid2>
          <Grid2 xs={2}>
            尺码: {editorMode === "hood" ? config["hood-size"] : "均码"}
          </Grid2>
          <Grid2 xs={3}>
            <InputDialog keyText="日期" valueText={customerInfo['order_date']} handleChange={(context) => {handleCustomerInfoChange('order_date', context)}}/>
          </Grid2>
          <Grid2 xs={4}>
            {editorMode === "hood" ?
            <Stack spacing={2}>
              <ColorBlock
                desp="主体面部"
                color={config["hood-base-color"]}
                textOverwrite={{
                  "black": blackConfig["hood-base-color"] ? "光皮黑": "哑光黑",
                }}
                handleClick={()=> {
                  handleBlackConfigChange("hood-base-color")
                }}
                forceChinese
              />
              <ColorBlock
                desp="头盖"
                color={config["crown-color"]}
                textOverwrite={{
                  "black": blackConfig["crown-color"] ? "光皮黑": "哑光黑",
                }}
                handleClick={()=> {
                  handleBlackConfigChange("crown-color")
                }}
                forceChinese
              />
              <ColorBlock
                desp="眉毛"
                color={config["brow-color"]}
                textOverwrite={{
                  "black": blackConfig["brow-color"] ? "光皮黑": "哑光黑",
                }}
                handleClick={()=> {
                  handleBlackConfigChange("brow-color")
                }}
                forceChinese
              />
              <ColorBlock
                desp="鼻梁"
                color={config["bridge-color"]}
                textOverwrite={{
                  "black": blackConfig["bridge-color"] ? "光皮黑": "哑光黑",
                }}
                handleClick={()=> {
                  handleBlackConfigChange("bridge-color")
                }}
                forceChinese
              />
              <ColorBlock
                desp="鼻头"
                color={config["nose-color"]}
                textOverwrite={{
                  "black": blackConfig["nose-color"] ? "光皮黑": "哑光黑",
                }}
                handleClick={()=> {
                  handleBlackConfigChange("nose-color")
                }}
                forceChinese
              />
              <ColorBlock
                desp="脸部"
                color={config["muzzle-color"]}
                textOverwrite={{
                  "black": blackConfig["muzzle-color"] ? "光皮黑": "哑光黑",
                }}
                handleClick={()=> {
                  handleBlackConfigChange("muzzle-color")
                }}
                forceChinese
              />
              <ColorBlock
                desp="嘴唇"
                color={config["muzzle-lip-color"]}
                textOverwrite={{
                  "black": blackConfig["muzzle-lip-color"] ? "光皮黑": "哑光黑",
                }}
                handleClick={()=> {
                  handleBlackConfigChange("muzzle-lip-color")
                }}
                forceChinese
              />
              <ColorBlock
                desp="下巴"
                color={config["chin-color"]}
                textOverwrite={{
                  "black": blackConfig["chin-color"] ? "光皮黑": "哑光黑",
                }}
                handleClick={()=> {
                  handleBlackConfigChange("chin-color")
                }}
                forceChinese
              />
              {isHoodFaceDetailFamily(config['muzzle-style']) ? <React.Fragment>
              {config['muzzle-style'].startsWith('added') &&
              <ColorBlock
                desp="脸部A"
                color={config["muzzle-a-color"]}
                textOverwrite={{
                  "black": blackConfig["muzzle-a-color"] ? "光皮黑": "哑光黑",
                }}
                handleClick={()=> {
                  handleBlackConfigChange("muzzle-a-color")
                }}
                forceChinese
              />
              }
              {(config['muzzle-assoicate'] === 'mustache' || config['muzzle-assoicate'] === 'fur') &&
              <ColorBlock
                desp="脸部B"
                color={config["muzzle-b-color"]}
                textOverwrite={{
                  "black": blackConfig["muzzle-b-color"] ? "光皮黑": "哑光黑",
                }}
                handleClick={()=> {
                  handleBlackConfigChange("muzzle-b-color")
                }}
                forceChinese
              />
              }
              </React.Fragment> : <React.Fragment>
              {isHoodFaceDetailFamily(config['muzzle-style']) ? <React.Fragment>
              {config['muzzle-style'].startsWith('added') &&
              <ColorBlock
                desp="脸部A"
                color={config["muzzle-a-color"]}
                handleClick={()=> {
                  handleBlackConfigChange("muzzle-a-color")
                }}
                forceChinese
              />
              }
              {(config['muzzle-assoicate'] === 'mustache' || config['muzzle-assoicate'] === 'fur') &&
              <ColorBlock
                desp="脸部B"
                color={config["muzzle-b-color"]}
                handleClick={()=> {
                  handleBlackConfigChange("muzzle-b-color")
                }}
                forceChinese
              />
              }
              {config['nose-patch'] && <ColorBlock
                desp="鼻贴"
                color={config["nose-patch-color"]}
                handleClick={()=> {
                  handleBlackConfigChange("nose-patch-color")
                }}
                forceChinese
              />}
              </React.Fragment> : <React.Fragment>
              {config['nose-patch'] && <ColorBlock
                desp="鼻贴"
                color={config["nose-patch-color"]}
                handleClick={()=> {
                  handleBlackConfigChange("nose-patch-color")
                }}
                forceChinese
              />}
              <ColorBlock
                desp="胡子"
                color={config["whiskers-color"]}
                handleClick={()=> {
                  handleBlackConfigChange("whiskers-color")
                }}
                forceChinese
              />
              </React.Fragment>}
              </React.Fragment>}
              <ColorBlock
                desp="耳朵A"
                color={config["ear-a-color"]}
                textOverwrite={{
                  "black": blackConfig["ear-a-color"] ? "光皮黑": "哑光黑",
                }}
                handleClick={()=> {
                  handleBlackConfigChange("ear-a-color")
                }}
                forceChinese
              />
              <ColorBlock
                desp="耳朵B"
                color={config["ear-b-color"]}
                textOverwrite={{
                  "black": blackConfig["ear-b-color"] ? "光皮黑": "哑光黑",
                }}
                handleClick={()=> {
                  handleBlackConfigChange("ear-b-color")
                }}
                forceChinese
              />
              <ColorBlock
                desp="耳朵C"
                color={config["ear-c-color"]}
                textOverwrite={{
                  "black": blackConfig["ear-c-color"] ? "光皮黑": "哑光黑",
                }}
                handleClick={()=> {
                  handleBlackConfigChange("ear-c-color")
                }}
                forceChinese
              />
              <ColorBlock
                desp="鼻梁纽扣"
                color={config["bridge-button"]}
                forceChinese
              />
              <ColorBlock
                desp="脸颊纽扣（上）"
                color={config["muzzle-button"]}
                forceChinese
              />
              <ColorBlock
                desp="脸颊纽扣（下）"
                color={config["muzzle-button-b"]}
                forceChinese
              />
              <div style={{ display: 'flex', alignItems: 'center', padding: '0px', direction: 'row', justifyContent: 'flex-start', width: '100%' }}>
                <div>加耳朵孔&nbsp;:&nbsp;</div><Checkbox checked={config["ear-hole"]} style={{padding:"0px"}}/>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', padding: '0px', direction: 'row', justifyContent: 'flex-start', width: '100%' }}>
                <div>加眼镜孔&nbsp;:&nbsp;</div><Checkbox checked={config["glass-hole"]} style={{padding:"0px"}}/>
              </div>
            </Stack>:
            <Stack spacing={2}>
              <ColorBlock
                desp="鼻梁"
                color={config["bridge-color"]}
                handleClick={()=> {
                  handleBlackConfigChange("bridge-color")
                }}
                forceChinese
              />
              {isHoodFaceDetailFamily(config['muzzle-style']) ? <React.Fragment>
              {config['muzzle-style'].startsWith('added') &&
              <ColorBlock
                desp="脸部A"
                color={config["muzzle-a-color"]}
                handleClick={()=> {
                  handleBlackConfigChange("muzzle-a-color")
                }}
                forceChinese
              />
              }
              {(config['muzzle-assoicate'] === 'mustache' || config['muzzle-assoicate'] === 'fur') &&
              <ColorBlock
                desp="脸部B"
                color={config["muzzle-b-color"]}
                handleClick={()=> {
                  handleBlackConfigChange("muzzle-b-color")
                }}
                forceChinese
              />
              }
              {config['nose-patch'] && <ColorBlock
                desp="鼻贴"
                color={config["nose-patch-color"]}
                handleClick={()=> {
                  handleBlackConfigChange("nose-patch-color")
                }}
                forceChinese
              />}
              </React.Fragment> : <React.Fragment>
              {config['nose-patch'] && <ColorBlock
                desp="鼻贴"
                color={config["nose-patch-color"]}
                handleClick={()=> {
                  handleBlackConfigChange("nose-patch-color")
                }}
                forceChinese
              />}
              <ColorBlock
                desp="胡子"
                color={config["whiskers-color"]}
                handleClick={()=> {
                  handleBlackConfigChange("whiskers-color")
                }}
                forceChinese
              />
              </React.Fragment>}
              <ColorBlock
                desp="鼻头"
                color={config["nose-color"]}
                handleClick={()=> {
                  handleBlackConfigChange("nose-color")
                }}
                forceChinese
              />
              <ColorBlock
                desp="脸部"
                color={config["muzzle-color"]}
                handleClick={()=> {
                  handleBlackConfigChange("muzzle-color")
                }}
                forceChinese
              />
              <ColorBlock
                desp="嘴唇"
                color={config["muzzle-lip-color"]}
                handleClick={()=> {
                  handleBlackConfigChange("muzzle-lip-color")
                }}
                forceChinese
              />
              <ColorBlock
                desp="耳朵A"
                color={config["ear-a-color"]}
                handleClick={()=> {
                  handleBlackConfigChange("ear-a-color")
                }}
                forceChinese
              />
              <ColorBlock
                desp="耳朵B"
                color={config["ear-b-color"]}
                handleClick={()=> {
                  handleBlackConfigChange("ear-b-color")
                }}
                forceChinese
              />
              <ColorBlock
                desp="耳朵C"
                color={config["ear-c-color"]}
                handleClick={()=> {
                  handleBlackConfigChange("ear-c-color")
                }}
                forceChinese
              />
              <ColorBlock
                desp="耳朵纽扣"
                color={config["ear-button"]}
                forceChinese
              />
              <ColorBlock
                desp="脸颊纽扣（上）"
                color={config["muzzle-button"]}
                forceChinese
              />
               <ColorBlock
                desp="脸颊纽扣（下）"
                color={config["muzzle-button-b"]}
                forceChinese
              />
            </Stack>}
          </Grid2>
          <Grid2 xs={8}>
            <Hood config={config} height="7in" print={true} mode={editorMode}/>
          </Grid2>
        </Grid2>
      </Container>
    )
  }
  return (
    <Container style={{
      backgroundImage: `url(${LOGO})`,
      backgroundSize: '100% auto',
      backgroundRepeat: 'no-repeat',
      backgroundAttachment: 'fixed',
      backgroundPosition: 'Center' }}>
      {/* Shuffle button — top-left, outside the drawer */}
      <Fab
        variant="extended"
        size="medium"
        color="primary"
        sx={{ position: 'absolute', top: 15, left: 20, zIndex: 10 }}
        aria-label={t('app.shuffle')}
        onClick={handleShuffle}
        disabled={shuffling}
      >
        <ShuffleIcon sx={{ mr: 1 }} />
        {shuffling ? t('app.shuffling') : t('app.shuffle')}
      </Fab>
      {/* Menu Icon */}
      <Box sx={{
        position: 'absolute',
        top: 15,
        right: 20,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '8px',
        zIndex: 10,
      }}>
        <ToggleButtonGroup
          color="primary"
          value={editorMode}
          exclusive
          onChange={handleEditorChanged}
          aria-label="Editor Mode"
        >
          <ToggleButton value="hood">{t('app.hood')}</ToggleButton>
          <ToggleButton value="muzzle">{t('app.muzzle')}</ToggleButton>
        </ToggleButtonGroup>
        {editorMode === "hood" &&
        <Box component="section" sx={{ textAlign: 'right' }}>
          <Typography variant="body2">
            {t('app.sizeLabel')}: {config["hood-size"]}
          </Typography>
          <Typography variant="body2">
            {t('app.blackMaterial')}: {config["black-type-light"] ? t('app.blackTypeGlossy') : t('app.blackTypeMatte')}
          </Typography>
        </Box>
        }
      </Box>
      <Fab size="large" color="primary" sx={{
        position: 'absolute',
        bottom: 20,
        right: 20
      }} aria-label="menu" onClick={handleDrawerOpen}>
        <EditIcon />
      </Fab>
      <Hood config={config} mode={editorMode}/>
      <Paper variant='outlined' style={{ height: 90, width: 90}} sx={{
        position: 'absolute',
        bottom: 20,
        left: 20
      }}>
        <img alt="qrcode" src={qrcode} style={{ height: '100%', width: '100%' }}/>
      </Paper>
      {/* Drawer */}

      <Drawer anchor="right" open={drawerOpen} onClose={handleDrawerClose}>
        {/* Add your drawer content here */}
        {editorMode === "hood" ? <div style={{ width: '320px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '10px' }}>
            <Typography variant="h6">
              RULER
            </Typography>
            <LanguageSwitcher />
          </div>
          <Divider variant="middle">{t('section.size')}</Divider>
          <List>
            <ListItem>
            <Typography variant="subtitle1" style={{ margin: '10px', width: '80px' }}>
               {t('label.size')}</Typography>
            <FormControl fullWidth>
              <Select
                style={{width: '180px'}}
                value={config['hood-size']}
                onChange={handleSizeChange}
              >
                <MenuItem value="M">M (56-58cm)</MenuItem>
                <MenuItem value="L">L (58-60cm)</MenuItem>
                <MenuItem value="XL">XL (60-62cm)</MenuItem>
                <MenuItem value="XXL">XXL (63-65cm)</MenuItem>
              </Select>
            </FormControl>
            </ListItem>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                 {t('label.blackMaterial')}
              </Typography>
              <FormGroup>
                  <FormControlLabel control={<Checkbox checked={config["black-type-light"]} onChange={(event) => {
                    handleCheckChange("black-type-light", event.target.checked);
                }} />} label={t('app.blackTypeGlossy')} />
                </FormGroup>
                <FormGroup>
                  <FormControlLabel control={<Checkbox checked={!config["black-type-light"]} onChange={(event) => {
                    handleCheckChange("black-type-light", !event.target.checked);
                }} />} label={t('app.blackTypeMatte')} />
              </FormGroup>
            </ListItem>
          </List>
          <Divider variant="middle">{t('section.main')}</Divider>
          <List>
            <ListItem>
            <Typography variant="subtitle1" style={{ margin: '10px' }}>
               {t('label.hoodBaseFace')}
            </Typography>
            {renderColorSelect("hood-base-color", { ignore: ["texture-1", "texture-2", "metal", "tiffany", "green", "lake-blue", "peach-red", "grass-green", "dark-chocolate"], extraText: {"red": t('colorPrice.red'),
                "orange": t('colorPrice.orange'),
                "yellow": t('colorPrice.yellow'),
                "light-blue": t('colorPrice.lightBlue'),
                "blue": t('colorPrice.blue'),
                "brown": t('colorPrice.brown'),
                "pink": t('colorPrice.pink'),
                "white": t('colorPrice.white'),
                "gray": t('colorPrice.gray'),
                "dark-blue": t('colorPrice.darkBlue'),
                "dark-gray": t('colorPrice.darkGray'),
                "dark-green": t('colorPrice.darkGreen'),
                "rose-red": t('colorPrice.roseRed'),
                "apricot": t('colorPrice.apricot'),
                "pupple": t('colorPrice.purple'),} })}
            </ListItem>
            <ListItem>
            <Typography variant="subtitle1" style={{ margin: '10px' }}>
               {t('label.crown')}
            </Typography>
            {renderColorSelect("crown-color", { ignore: ["texture-1", "texture-2", "metal", "peach-red", "grass-green", "dark-chocolate"] })}
            </ListItem>
          </List>
          <Divider variant="middle">{t('section.eyebrow')}</Divider>
          <List>
            <ListItem>
              <Box sx={{ flexGrow: 1 }}>
                <Grid2 container spacing={2}>
                  <Grid2 xs={4}>
                    <ItemPicker text={t('item.eyebrowDefault')} selected={config['eyebrow-style'] === 'default'} image={EyeDefault} onClick={() => {handleEyeBrowStyleChange('default');}}/>
                  </Grid2>
                  <Grid2 xs={4}>
                    <ItemPicker text={t('item.eyebrowDou')} selected={config['eyebrow-style'] === 'dou'} image={EyeDou} onClick={() => {handleEyeBrowStyleChange('dou')}}/>
                  </Grid2>
                  <Grid2 xs={4}>
                    <ItemPicker text={t('item.eyebrowFrame')} selected={config['eyebrow-style'] === 'frame'} image={EyeFrame} onClick={() => {handleEyeBrowStyleChange('frame')}}/>
                  </Grid2>
                </Grid2>
              </Box>
            </ListItem>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                 {t('label.eyebrow')}
              </Typography>
              {renderColorSelect("brow-color")}
            </ListItem>
          </List>
          <Divider variant="middle">{t('section.face')}</Divider>
          <List>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                 {t('label.bridge')}</Typography>
              {renderColorSelect("bridge-color", { ignore: ["metal"] })}
            </ListItem>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                 {t('label.nose')}</Typography>
              {renderColorSelect("nose-color", { extraText: {"texture-black": t('color.textureBlackSmall')} })}
            </ListItem>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                 {t('label.face')}</Typography>
              {renderColorSelect("muzzle-color")}
            </ListItem>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                 {t('label.lip')}</Typography>
              {renderColorSelect("muzzle-lip-color")}
            </ListItem>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                 {t('label.chin')}</Typography>
              {renderColorSelect("chin-color", { ignore: ["texture-1", "texture-2", "metal"] })}
            </ListItem>
          </List>
                    <Divider variant="middle">{t('section.faceDetails')}</Divider>
          <List>
            {/* muzzle style picker — 圆脸/FUR are hood styles, style1/style2 are muzzle styles */}
            <ListItem>
              <Box sx={{ flexGrow: 1 }}>
                <Grid2 container spacing={2}>
                  <Grid2 xs={4}>
                    <ItemPicker selected={config['muzzle-style'] === 'default' || config['muzzle-style'] === 'added-default'} text={t('item.faceRound')} image={MuzzleDefault} onClick={() => {handleMuzzleStyleChange('default');}}/>
                  </Grid2>
                  <Grid2 xs={4}>
                    <ItemPicker selected={config['muzzle-style'] === 'fur' || config['muzzle-style'] === 'added-fur'} text={t('item.faceFur')} image={MuzzleFur} onClick={() => {handleMuzzleStyleChange('fur');}}/>
                  </Grid2>
                  <Grid2 xs={4}>
                    <ItemPicker selected={config['muzzle-style'] === 'style1'} text={t('item.muzzleStyle1')} image={MuzzleDefaultDefault} onClick={() => {handleMuzzleStyleChange('style1');}}/>
                  </Grid2>
                  <Grid2 xs={4}>
                    <ItemPicker selected={config['muzzle-style'] === 'style2'} text={t('item.muzzleStyle2')} image={MuzzleFurDefault} onClick={() => {handleMuzzleStyleChange('style2');}}/>
                  </Grid2>
                </Grid2>
              </Box>
            </ListItem>
            {isHoodFaceDetailFamily(config['muzzle-style']) ? <React.Fragment>
            <ListItem>
              <Box sx={{ flexGrow: 1 }}>
                <Grid2 container spacing={2}>
                  <Grid2 xs={4}>
                    <ItemPicker selected={config['muzzle-style'].startsWith('added')} text={t('item.faceCheek')} image={config['muzzle-style'] === 'default' || config['muzzle-style'] === 'added-default'? AddedMuzzleDefault : AddedMuzzleFur } onClick={() => {handleMuzzleAddedChange(!config['muzzle-style'].startsWith('added'));}}/>
                  </Grid2>
                </Grid2>
              </Box>
            </ListItem>
            <ListItem>
              <Box sx={{ flexGrow: 1 }}>
                <Grid2 container spacing={2}>
                  <Grid2 xs={4}>
                    <ItemPicker selected={config['muzzle-assoicate'] === 'default'} text={t('item.muzzleThreeHoles')} image={config['muzzle-style'] === 'default' || config['muzzle-style'] === 'added-default'? MuzzleAssoicateDefault : FurMuzzleAssoicateDefault} onClick={() => {handleMuzzleAssoicateChange('default');}}/>
                  </Grid2>
                  <Grid2 xs={4}>
                    <ItemPicker selected={config['muzzle-assoicate'] === 'mustache'} text={t('item.muzzleMustache')} image={config['muzzle-style'] === 'default' || config['muzzle-style'] === 'added-default'? MuzzleAssoicateMustache : FurMuzzleAssoicateMustache} onClick={() => {handleMuzzleAssoicateChange('mustache');}}/>
                  </Grid2>
                  <Grid2 xs={4}>
                    <ItemPicker selected={config['muzzle-assoicate'] === 'fur'} text={t('item.muzzleFur')} image={config['muzzle-style'] === 'default' || config['muzzle-style'] === 'added-default'? MuzzleAssoicateFur : FurMuzzleAssoicateFur} onClick={() => {handleMuzzleAssoicateChange('fur');}}/>
                  </Grid2>
                </Grid2>
              </Box>
            </ListItem>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                A
              </Typography>
              {renderColorSelect("muzzle-a-color")}
            </ListItem>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                B
              </Typography>
              {renderColorSelect("muzzle-b-color")}
            </ListItem>
            {
              config['muzzle-assoicate'] === 'default' ?
            <ListItem>
              <Box sx={{ flexGrow: 1 }}>
                <Grid2 container spacing={2}>
                  <Grid2 xs={4}>
                    <ItemPicker selected={config["spikes-style"] === "spikes1"} text={t('item.spikesA')} image={config['muzzle-style'] === 'default' || config['muzzle-style'] === 'added-default'? SpikesStyleAMuzzle : SpikesStyleAFur} onClick={() => {handleSpikesStyleChange(config["spikes-style"] === "spikes1" ? "default": "spikes1");}}/>
                  </Grid2>
                  <Grid2 xs={4}>
                    <ItemPicker selected={config["spikes-style"] === "spikes2"} text={t('item.spikesB')} image={config['muzzle-style'] === 'default' || config['muzzle-style'] === 'added-default'? SpikesStyleBMuzzle : SpikesStyleBFur} onClick={() => {handleSpikesStyleChange(config["spikes-style"] === "spikes2" ? "default": "spikes2");}}/>
                  </Grid2>
                </Grid2>
              </Box>
            </ListItem> : <ListItem/>
          }
            {/* 鼻贴 — usable in both style families */}
            <ListItem>
              <Box sx={{ flexGrow: 1 }}>
                <Grid2 container spacing={2}>
                  <Grid2 xs={4}>
                    <ItemPicker selected={config['nose-patch']} text={t('item.nosePatch')} image={MuzzleLip} onClick={() => {handleNosePatchChange(!config['nose-patch']);}}/>
                  </Grid2>
                </Grid2>
              </Box>
            </ListItem>
            {config['nose-patch'] ?
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                 {t('label.nosePatch')}</Typography>
              {renderColorSelect("nose-patch-color", { blackText: false })}
            </ListItem>: <div/>}
            </React.Fragment> : <React.Fragment>
            <ListItem>
              <Box sx={{ flexGrow: 1 }}>
                <Grid2 container spacing={2}>
                  <Grid2 xs={4}>
                    <ItemPicker selected={config['nose-patch']} text={t('item.nosePatch')} image={MuzzleLip} onClick={() => {handleNosePatchChange(!config['nose-patch']);}}/>
                  </Grid2>
                </Grid2>
              </Box>
            </ListItem>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                 {t('label.whiskers')}</Typography>
              {renderColorSelect("whiskers-color", { blackText: false })}
            </ListItem>
            {config['nose-patch'] ?
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                 {t('label.nosePatch')}</Typography>
              {renderColorSelect("nose-patch-color", { blackText: false })}
            </ListItem>: <div/>}
            </React.Fragment>}
          </List>
          <Divider variant="middle">{t('section.earDetails')}</Divider>
          <List>
            <ListItem>
              <Box sx={{ flexGrow: 1 }}>
                <Grid2 container spacing={2}>
                  <Grid2 xs={4}>
                    <ItemPicker selected text={t('item.earDetails')} image={Ear}/>
                  </Grid2>
                </Grid2>
              </Box>
            </ListItem>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                A
              </Typography>
              {renderColorSelect("ear-a-color", { ignore: ["metal", "dark-gray"] })}
            </ListItem>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                B
              </Typography>
              {renderColorSelect("ear-b-color", { ignore: ["metal", "dark-gray"] })}
            </ListItem>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                C
              </Typography>
              {renderColorSelect("ear-c-color", { ignore: ["metal", "dark-gray"] })}
            </ListItem>
          </List>
          <Divider variant="middle">{t('section.other')}</Divider>
          <List>
            <ListItem>
              <FormGroup>
                <FormControlLabel control={<Checkbox checked={config["ear-hole"]} onChange={(event) => {
                  handleCheckChange("ear-hole", event.target.checked);
              }} />} label={t('checkbox.earHole')} />
              </FormGroup>
            </ListItem>
            <ListItem>
              <FormGroup>
                <FormControlLabel control={<Checkbox checked={config["glass-hole"]} onChange={(event) => {
                  handleCheckChange("glass-hole", event.target.checked);
              }} />} label={t('checkbox.glassHole')} />
              </FormGroup>
            </ListItem>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                 {t('label.bridgeButton')}
              </Typography>
              {renderColorSelect("bridge-button", { colorItems: buttonColorList, description: t('desc.colorMetalButton'), blackText: false, extraText: {"light-blue": t('color.blueSimple'),} })}
            </ListItem>
            <ListItem>
               <Typography variant="subtitle1" style={{ margin: '10px' }}>
                 {t('label.muzzleButtonUpper')}
               </Typography>
               {renderColorSelect("muzzle-button", { colorItems: buttonColorList, description: t('desc.colorMetalButton'), blackText: false, extraText: {"light-blue": t('color.blueSimple'),} })}
            </ListItem>
            <ListItem>
               <Typography variant="subtitle1" style={{ margin: '10px' }}>
                 {t('label.muzzleButtonLower')}
               </Typography>
               {renderColorSelect("muzzle-button-b", { colorItems: buttonColorList, description: t('desc.colorMetalButton'), blackText: false, extraText: {"light-blue": t('color.blueSimple'),} })}
            </ListItem>
          </List>
        </div>:
        <div style={{ width: '320px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '10px' }}>
            <Typography variant="h6">
              RULER
            </Typography>
            <LanguageSwitcher />
          </div>
          <Divider variant="middle">{t('section.size')}</Divider>
          <List>
            <ListItem>
            <Typography variant="subtitle1" style={{ margin: '10px', width: '80px' }}>
               {t('label.size')}</Typography>
            <FormControl fullWidth>
              <Select
                style={{width: '180px'}}
                value={"default"}
              >
                <MenuItem value="default">{t('app.oneSize')}</MenuItem>
              </Select>
            </FormControl>
            </ListItem>
          </List>
          <Divider variant="middle">{t('section.face')}</Divider>
          <List>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                 {t('label.bridge')}</Typography>
              {renderColorSelect("bridge-color", { blackText: false })}
            </ListItem>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                 {t('label.nose')}</Typography>
              {renderColorSelect("nose-color", { blackText: false, extraText: {"texture-black": t('color.textureBlackSmall')} })}
            </ListItem>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                 {t('label.face')}</Typography>
              {renderColorSelect("muzzle-color", { blackText: false })}
            </ListItem>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                 {t('label.lip')}</Typography>
              {renderColorSelect("muzzle-lip-color", { blackText: false })}
            </ListItem>
          </List>
                    <Divider variant="middle">{t('section.faceDetails')}</Divider>
          <List>
            {/* muzzle style picker — 圆脸/FUR are hood styles, style1/style2 are muzzle styles */}
            <ListItem>
              <Box sx={{ flexGrow: 1 }}>
                <Grid2 container spacing={2}>
                  <Grid2 xs={4}>
                    <ItemPicker selected={config['muzzle-style'] === 'default' || config['muzzle-style'] === 'added-default'} text={t('item.faceRound')} image={MuzzleDefault} onClick={() => {handleMuzzleStyleChange('default');}}/>
                  </Grid2>
                  <Grid2 xs={4}>
                    <ItemPicker selected={config['muzzle-style'] === 'fur' || config['muzzle-style'] === 'added-fur'} text={t('item.faceFur')} image={MuzzleFur} onClick={() => {handleMuzzleStyleChange('fur');}}/>
                  </Grid2>
                  <Grid2 xs={4}>
                    <ItemPicker selected={config['muzzle-style'] === 'style1'} text={t('item.muzzleStyle1')} image={MuzzleDefaultDefault} onClick={() => {handleMuzzleStyleChange('style1');}}/>
                  </Grid2>
                  <Grid2 xs={4}>
                    <ItemPicker selected={config['muzzle-style'] === 'style2'} text={t('item.muzzleStyle2')} image={MuzzleFurDefault} onClick={() => {handleMuzzleStyleChange('style2');}}/>
                  </Grid2>
                </Grid2>
              </Box>
            </ListItem>
            {isHoodFaceDetailFamily(config['muzzle-style']) ? <React.Fragment>
            <ListItem>
              <Box sx={{ flexGrow: 1 }}>
                <Grid2 container spacing={2}>
                  <Grid2 xs={4}>
                    <ItemPicker selected={config['muzzle-style'].startsWith('added')} text={t('item.faceCheek')} image={config['muzzle-style'] === 'default' || config['muzzle-style'] === 'added-default'? AddedMuzzleDefault : AddedMuzzleFur } onClick={() => {handleMuzzleAddedChange(!config['muzzle-style'].startsWith('added'));}}/>
                  </Grid2>
                </Grid2>
              </Box>
            </ListItem>
            <ListItem>
              <Box sx={{ flexGrow: 1 }}>
                <Grid2 container spacing={2}>
                  <Grid2 xs={4}>
                    <ItemPicker selected={config['muzzle-assoicate'] === 'default'} text={t('item.muzzleThreeHoles')} image={config['muzzle-style'] === 'default' || config['muzzle-style'] === 'added-default'? MuzzleAssoicateDefault : FurMuzzleAssoicateDefault} onClick={() => {handleMuzzleAssoicateChange('default');}}/>
                  </Grid2>
                  <Grid2 xs={4}>
                    <ItemPicker selected={config['muzzle-assoicate'] === 'mustache'} text={t('item.muzzleMustache')} image={config['muzzle-style'] === 'default' || config['muzzle-style'] === 'added-default'? MuzzleAssoicateMustache : FurMuzzleAssoicateMustache} onClick={() => {handleMuzzleAssoicateChange('mustache');}}/>
                  </Grid2>
                  <Grid2 xs={4}>
                    <ItemPicker selected={config['muzzle-assoicate'] === 'fur'} text={t('item.muzzleFur')} image={config['muzzle-style'] === 'default' || config['muzzle-style'] === 'added-default'? MuzzleAssoicateFur : FurMuzzleAssoicateFur} onClick={() => {handleMuzzleAssoicateChange('fur');}}/>
                  </Grid2>
                </Grid2>
              </Box>
            </ListItem>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                A
              </Typography>
              {renderColorSelect("muzzle-a-color")}
            </ListItem>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                B
              </Typography>
              {renderColorSelect("muzzle-b-color")}
            </ListItem>
            {
              config['muzzle-assoicate'] === 'default' ?
            <ListItem>
              <Box sx={{ flexGrow: 1 }}>
                <Grid2 container spacing={2}>
                  <Grid2 xs={4}>
                    <ItemPicker selected={config["spikes-style"] === "spikes1"} text={t('item.spikesA')} image={config['muzzle-style'] === 'default' || config['muzzle-style'] === 'added-default'? SpikesStyleAMuzzle : SpikesStyleAFur} onClick={() => {handleSpikesStyleChange(config["spikes-style"] === "spikes1" ? "default": "spikes1");}}/>
                  </Grid2>
                  <Grid2 xs={4}>
                    <ItemPicker selected={config["spikes-style"] === "spikes2"} text={t('item.spikesB')} image={config['muzzle-style'] === 'default' || config['muzzle-style'] === 'added-default'? SpikesStyleBMuzzle : SpikesStyleBFur} onClick={() => {handleSpikesStyleChange(config["spikes-style"] === "spikes2" ? "default": "spikes2");}}/>
                  </Grid2>
                </Grid2>
              </Box>
            </ListItem> : <ListItem/>
          }
            {/* 鼻贴 — usable in both style families */}
            <ListItem>
              <Box sx={{ flexGrow: 1 }}>
                <Grid2 container spacing={2}>
                  <Grid2 xs={4}>
                    <ItemPicker selected={config['nose-patch']} text={t('item.nosePatch')} image={MuzzleLip} onClick={() => {handleNosePatchChange(!config['nose-patch']);}}/>
                  </Grid2>
                </Grid2>
              </Box>
            </ListItem>
            {config['nose-patch'] ?
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                 {t('label.nosePatch')}</Typography>
              {renderColorSelect("nose-patch-color", { blackText: false })}
            </ListItem>: <div/>}
            </React.Fragment> : <React.Fragment>
            <ListItem>
              <Box sx={{ flexGrow: 1 }}>
                <Grid2 container spacing={2}>
                  <Grid2 xs={4}>
                    <ItemPicker selected={config['nose-patch']} text={t('item.nosePatch')} image={MuzzleLip} onClick={() => {handleNosePatchChange(!config['nose-patch']);}}/>
                  </Grid2>
                </Grid2>
              </Box>
            </ListItem>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                 {t('label.whiskers')}</Typography>
              {renderColorSelect("whiskers-color", { blackText: false })}
            </ListItem>
            {config['nose-patch'] ?
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                 {t('label.nosePatch')}</Typography>
              {renderColorSelect("nose-patch-color", { blackText: false })}
            </ListItem>: <div/>}
            </React.Fragment>}
          </List>
          <Divider variant="middle">{t('section.earDetails')}</Divider>
          <List>
            <ListItem>
              <Box sx={{ flexGrow: 1 }}>
                <Grid2 container spacing={2}>
                  <Grid2 xs={4}>
                    <ItemPicker selected text={t('item.earDetails')} image={MuzzleEar}/>
                  </Grid2>
                </Grid2>
              </Box>
            </ListItem>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                A
              </Typography>
              {renderColorSelect("ear-a-color", { ignore: ["metal", "dark-gray"], blackText: false })}
            </ListItem>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                B
              </Typography>
              {renderColorSelect("ear-b-color", { ignore: ["metal", "dark-gray"], blackText: false })}
            </ListItem>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                C
              </Typography>
              {renderColorSelect("ear-c-color", { ignore: ["metal", "dark-gray"], blackText: false })}
            </ListItem>
          </List>
          <Divider variant="middle">{t('section.other')}</Divider>
          <List>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                 {t('label.earButton')}</Typography>
              {renderColorSelect("ear-button", { colorItems: buttonColorList, ignore: ["metal"], description: t('desc.colorButton'), blackText: false, extraText: {"light-blue": t('color.blueSimple'),} })}
            </ListItem>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                 {t('label.muzzleButtonUpper')}</Typography>
              {renderColorSelect("muzzle-button", { colorItems: buttonColorList, ignore: ["metal"], description: t('desc.colorButton'), blackText: false, extraText: {"light-blue": t('color.blueSimple'),} })}
            </ListItem>
            <ListItem>
              <Typography variant="subtitle1" style={{ margin: '10px' }}>
                 {t('label.muzzleButtonLower')}</Typography>
              {renderColorSelect("muzzle-button-b", { colorItems: buttonColorList, ignore: ["metal"], description: t('desc.colorButton'), blackText: false, extraText: {"light-blue": t('color.blueSimple'),} })}
            </ListItem>
          </List>
        </div>}
        <div style={{ display: 'flex', flexDirection: 'column', margin: '20px', gap: '8px' }}>
          <Button variant="outlined" onClick={handleDrawerClose} startIcon={<CloseIcon />} fullWidth>
             {t('drawer.close')}</Button>
          <SaveLoadPopUp config={config} editorMode={editorMode} />
          <Button variant="outlined" onClick={handleResetToDefault} startIcon={<DeleteIcon />} color="error" fullWidth>
             {t('drawer.reset')}</Button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-around', margin: '20px' }}>
          <Typography variant="caption" style={{ margin: '10px', color: '#BBBBBB' }}>
            {t('footer.attribution')}
          </Typography>
        </div>
      </Drawer>
    </Container>
  );
}

export default App;
